<script setup lang="ts">
import { computed, ref } from "vue";
import { useUserStore } from "../../stores/user";
import { useQueryStore } from "../../stores/query";
import useMobileBreakpoint from "../../composables/useMobileBreakpoint";
import { getEnvVar } from "../../utils/env";
import {
  getChildAges,
  parseDefaultDate,
  getStringParam,
} from "../../utils/searchParams";
import { convertToDate, nightBetweenDates } from "../../utils/dateUtils";
import BaseSearchForm from "../SearchForm/BaseSearchForm.vue";
import type { Entity } from "../LocationField/adapter";
import { GuestsData, intParser } from "vacv-components";
import type { ProductType } from "../../../types/common";
import { ModifySearchMode } from "../../../types/common";
import { TabConfig } from "../../../types/tabs";

export interface Props {
  tabConfig: TabConfig;
  productType: ProductType;
}
const props = defineProps<Props>();

const emit = defineEmits<{
  "search-mode": [mode: ModifySearchMode];
}>();

const MIN_STAY = 1;
const tabConfig = computed(() => {
  return {
    ...props.tabConfig,
    minStay: MIN_STAY,
  };
});

const { isMobile } = useMobileBreakpoint();

const userStore = useUserStore();
const queryStore = useQueryStore(props.tabConfig.id!);

const searchParams = new URLSearchParams(window.location.search);

function createEntityFromSearchParams(name: string): Entity {
  return {
    destinationId: "",
    name,
    code: getStringParam(
      searchParams,
      "arrivalLocationName",
      "arrivalLocation"
    ),
    tts: false,
    type: searchParams.get("arrivalLocationSuggestionType"),
    useSPABookingFlow: false,
    cubaDestination: searchParams.get("cubaDestination") === "true",
    ttsDestinationCode: searchParams.get("dest_dep")?.toString(),
    ttsHotelCode: getStringParam(searchParams, "", "no_hotel"),
  } as Entity;
}

const arrivalLocationName = searchParams.get("arrivalLocationName") ?? "";
const arrivalEntity = props.tabConfig.isModifySearch
  ? createEntityFromSearchParams(arrivalLocationName)
  : null;
const departureDate = parseDefaultDate(searchParams, "checkInDate", 2);
const returnDate = parseDefaultDate(searchParams, "checkOutDate", 9);
const departureDateObject = convertToDate(departureDate, "dd/MM/yyyy", false);
const returnDateObject = convertToDate(returnDate, "dd/MM/yyyy", false);
const totalRooms = intParser(searchParams.get("numberOfRooms")) ?? 1;
const childAges = getChildAges(searchParams);
const totalAdults = intParser(searchParams.get("nb_adult")) ?? 2;

/*
nb_child is the total number of children AND infants
totalInfants is the number of children under age 2
totalChildren is the number of children age 2 or higher (nb_child - infants)
childAges is an array of the only of the children only (excluding infants) for selecting the age in the dropdowns
See https://aircanadavacations.atlassian.net/browse/ACVW-16250
*/

const nb_child = intParser(searchParams.get("nb_child")) ?? 0;
const totalChildren = childAges.length;
const totalInfants = nb_child - totalChildren;
const totalGuests = totalAdults + totalChildren + totalInfants;
const numberOfNights = nightBetweenDates(departureDateObject, returnDateObject);

const guestsValue = ref<GuestsData>({
  totalInfants,
  totalChildren,
  totalAdults,
  totalRooms,
  childAges,
  isDataValid: true,
});

const formData = computed(() => ({
  departureEntity: undefined,
  arrivalEntity,
  departureDate,
  returnDate,
  departureDateObject,
  returnDateObject,
  guests: guestsValue.value,
  totalGuests,
  numberOfNights,
  initialCabinClass: undefined,
}));

const getSearchUrl = async () => {
  const isB2BUser = await userStore.isB2BUser();
  if (isB2BUser) {
    const sirevsid = await userStore.getSirevSid();
    return queryStore.buildHotelSearchB2BUrl(sirevsid);
  }

  const useHotelSpa = getEnvVar("VITE_USE_HOTELS_SPA_BOOKING_FLOW") === "true";
  if (useHotelSpa) {
    return queryStore.buildSpaHotelSearchUrlParams();
  }

  const userJWTInfo = await userStore.getUserInfo();
  return queryStore.buildTTSHotelSearchUrlParams(userJWTInfo, isMobile.value);
};

async function submitHotelSearch() {
  const url = await getSearchUrl();
  window.open(url, "_self");
}

function emitUpdateMode() {
  emit("search-mode", ModifySearchMode.update);
}

function emitViewMode() {
  emit("search-mode", ModifySearchMode.view);
}
</script>

<template>
  <div
    class="search-content-panel"
    :id="tabConfig.id"
    role="tabpanel"
    :aria-labelledby="'btn-' + tabConfig.id"
  >
    <BaseSearchForm
      :tab-config="tabConfig"
      :product-type="productType"
      :form-data="formData"
      player-name="Hotel"
      @modify="emitUpdateMode"
      @show-cuba-popup="emitViewMode"
      @submit="submitHotelSearch"
    />
  </div>
</template>
